import { client, handleRequest, handleRequestBody } from "./client.ts";
import type { Construction } from "./constructions.ts";
import type { StageKind } from "./stages.ts";

export interface StageMeasurement {
	id: string;
	stage_id: string;
	stage_name?: string;
	stage_item_number?: string;
	measured_on: string;
	progress_percent: number;
	measured_cents?: number;
	note?: string;
	measured_by_id?: string;
	measured_by_name?: string;
	created_at: string;
	updated_at: string;
}

export interface ListMeasurementsParams {
	stageId?: string;
	includeDescendants?: boolean;
	from?: string;
	to?: string;
	page?: number;
	pageSize?: number;
}

export interface ListMeasurementsResponse {
	count: number;
	items: StageMeasurement[];
	construction: Construction;
}

export function listMeasurements(
	constructionId: string,
	params: ListMeasurementsParams = {},
): Promise<ListMeasurementsResponse> {
	return handleRequestBody(() =>
		client.get<ListMeasurementsResponse>(
			`/constructions/${constructionId}/measurements`,
			{
				params: {
					stage_id: params.stageId,
					include_descendants: params.includeDescendants || undefined,
					from: params.from,
					to: params.to,
					page: params.page,
					page_size: params.pageSize,
				},
			},
		),
	);
}

export interface StageRollup {
	stage_id: string;
	percent: number;
	measured_cents?: number;
}

export interface ListLatestMeasurementsResponse {
	items: StageMeasurement[];
	rollups?: StageRollup[];
	construction: Construction;
}

export function listLatestMeasurements(
	constructionId: string,
): Promise<ListLatestMeasurementsResponse> {
	return handleRequestBody(() =>
		client.get<ListLatestMeasurementsResponse>(
			`/constructions/${constructionId}/measurements/latest`,
		),
	);
}

export interface UpsertMeasurementInput {
	measuredOn: string;
	progressPercent: number;
	note?: string;
}

interface MeasurementResponse {
	measurement: StageMeasurement;
}

export async function upsertStageMeasurement(
	constructionId: string,
	stageId: string,
	input: UpsertMeasurementInput,
): Promise<StageMeasurement> {
	const { measurement } = await handleRequestBody(() =>
		client.put<MeasurementResponse>(
			`/constructions/${constructionId}/stages/${stageId}/measurements`,
			{
				measured_on: input.measuredOn,
				progress_percent: input.progressPercent,
				note: input.note ?? "",
			},
		),
	);
	return measurement;
}

export async function deleteMeasurement(
	constructionId: string,
	measurementId: string,
): Promise<void> {
	await handleRequest(() =>
		client.delete(
			`/constructions/${constructionId}/measurements/${measurementId}`,
		),
	);
}

export interface MeasurementBulletinRow {
	stage_id: string;
	item_number: string;
	kind: StageKind;
	name: string;
	depth: number;
	cancelled: boolean;
	previous_percent: number;
	period_percent: number;
	accumulated_percent: number;
	value_cents?: number;
	previous_cents?: number;
	period_cents?: number;
	accumulated_cents?: number;
	remaining_cents?: number;
	measured_on: string[] | null;
}

export interface MeasurementBulletinTotals {
	value_cents: number;
	previous_cents: number;
	period_cents: number;
	accumulated_cents: number;
	remaining_cents: number;
}

export interface MeasurementBulletinResponse {
	from: string;
	to: string;
	valued: boolean;
	rows: MeasurementBulletinRow[];
	totals?: MeasurementBulletinTotals;
	construction: Construction;
}

export function getMeasurementBulletin(
	constructionId: string,
	from: string,
	to: string,
): Promise<MeasurementBulletinResponse> {
	return handleRequestBody(() =>
		client.get<MeasurementBulletinResponse>(
			`/constructions/${constructionId}/measurements/bulletin`,
			{ params: { from, to } },
		),
	);
}
